import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { formatTimestampOr } from "../lib/formatTime.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { ConnectorEventLog } from "../components/ConnectorEventLog.js";
import { AiUsageDashboard } from "../components/AiUsageDashboard.js";
import { UsTimeZoneSelect } from "../components/UsTimeZoneSelect.js";
import {
  CONNECTOR_DIALECT_OPTIONS,
  CONNECTOR_KIND_OPTIONS,
  STATUS_LABELS,
  STATUS_PILL_CLASSES,
  type ConnectorDialect,
  type ConnectorKind,
  type ConnectorStatus,
} from "../lib/connectorVocab.js";

type Tenant = {
  id: string;
  name: string;
  slug: string;
  auth_mode: "atlas_local" | "oidc" | "saml";
  billing_contact_name: string | null;
  billing_contact_email: string | null;
  seat_limit: number | null;
  timezone: string;
  /** ADR-0216 Amendment 5: the calendar report periods are counted in. */
  week_start_day: string;
  fiscal_year_start_month: number;
  ai_calls_per_hour_limit: number | null;
  ai_monthly_token_limit: number | null;
  created_at: string;
};

const WEEK_DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"] as const;

type SeatUsage = {
  seat_used: number;
  active_users: number;
  invited_users: number;
};

type BreakGlassContact = {
  id: string;
  tenant_id: string;
  email: string;
  verified_at: string | null;
  active: boolean;
};


type InitialAdmin = {
  id: string;
  email: string;
  status: "invited" | "active" | "disabled" | "locked";
  role: "admin" | "builder" | "viewer";
  password_set: boolean;
};

type LatestInvite = {
  id: string;
  email: string;
  created_at: string;
  expires_at: string;
  consumed_at: string | null;
};

// ADR-0161 — the connector vocabularies now live in one console module held to
// the hub by the G9 parity harness. The dialect union used to be hand-typed
// here AND in TenantAdminConnectors.tsx, with nothing keeping either current.

type Connector = {
  id: string;
  tenant_id: string;
  name: string;
  version: string;
  protocol: string;
  capabilities: string[];
  dialect: ConnectorDialect | null;
  /** ADR-0161 — which system is on the other end. Total: never null. */
  kind: ConnectorKind;
  is_default: boolean;
  last_heartbeat_at: string | null;
  status: ConnectorStatus;
  status_reasons: string[];
  last_poll_at: string | null;
  last_execution_assigned_at: string | null;
  last_execution_completed_at: string | null;
  can_manage_actions: boolean;
};

type CreatedConnector = {
  id: string;
};

function connectorIdShort(id: string) {
  return `${id.slice(0, 8)}-…`;
}

/**
 * Copy text to clipboard with fallback for non-secure contexts.
 * Tries navigator.clipboard first (requires HTTPS), then falls back to execCommand.
 */
async function copyToClipboard(text: string): Promise<void> {
  // Try modern clipboard API first (requires secure context/HTTPS)
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch (err) {
      // Fall through to fallback method
    }
  }

  // Fallback: use temporary textarea + execCommand
  // This works in more contexts but also requires user interaction
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.left = "-999999px";
  textarea.style.top = "-999999px";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();

  try {
    const success = document.execCommand("copy");
    document.body.removeChild(textarea);
    if (!success) {
      throw new Error("execCommand('copy') returned false");
    }
  } catch (err) {
    document.body.removeChild(textarea);
    throw err;
  }
}

export function AdminTenantDetails() {
  const { tenantId } = useParams();
  const nav = useNavigate();
  const toast = useToast();

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [seats, setSeats] = useState<SeatUsage | null>(null);
  const [contacts, setContacts] = useState<BreakGlassContact[]>([]);
  const [initialAdmin, setInitialAdmin] = useState<InitialAdmin | null>(null);
  const [latestInvite, setLatestInvite] = useState<LatestInvite | null>(null);
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [connectorsLoading, setConnectorsLoading] = useState(false);
  const [connectorsError, setConnectorsError] = useState<string | null>(null);
  const [connectorModalOpen, setConnectorModalOpen] = useState(false);
  const [connectorName, setConnectorName] = useState("");
  const [creatingConnector, setCreatingConnector] = useState(false);
  const [createdConnector, setCreatedConnector] = useState<CreatedConnector | null>(null);
  const [selectedConnectorId, setSelectedConnectorId] = useState<string | null>(null);
  const [restartNotice, setRestartNotice] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [authMode, setAuthMode] = useState<Tenant["auth_mode"]>("atlas_local");
  const [billingName, setBillingName] = useState("");
  const [billingEmail, setBillingEmail] = useState("");
  const [seatLimitInput, setSeatLimitInput] = useState<string>(""); // empty => null
  const [timezoneInput, setTimezoneInput] = useState("");
  const [weekStartInput, setWeekStartInput] = useState("monday");
  const [fiscalStartInput, setFiscalStartInput] = useState(1);
  const [aiHourlyLimitInput, setAiHourlyLimitInput] = useState("");
  const [aiMonthlyTokenInput, setAiMonthlyTokenInput] = useState("");
  const [deleteName, setDeleteName] = useState("");
  const [initialAdminEmail, setInitialAdminEmail] = useState("");
  const tenantHeaders: Record<string, string> | null = tenantId ? { "x-tenant-id": tenantId } : null;

  const seatLimitParsed = useMemo(() => {
    const v = seatLimitInput.trim();
    if (v === "") return null;
    const n = Number(v);
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) return "invalid" as const;
    return n;
  }, [seatLimitInput]);

  async function refresh() {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const [detail, ia] = await Promise.all([
        apiFetch<{ tenant: Tenant; seats: SeatUsage; break_glass_contacts: BreakGlassContact[] }>(`/api/admin/tenants/${tenantId}`),
        apiFetch<{
          tenant_admin: InitialAdmin | null;
          latest_invite: LatestInvite | null;
        }>(`/api/admin/tenants/${tenantId}/tenant-admin`)
      ]);
      setTenant(detail.tenant);
      setSeats(detail.seats);
      setContacts(detail.break_glass_contacts);
      setName(detail.tenant.name);
      setAuthMode(detail.tenant.auth_mode);
      setBillingName(detail.tenant.billing_contact_name ?? "");
      setBillingEmail(detail.tenant.billing_contact_email ?? "");
      setSeatLimitInput(detail.tenant.seat_limit == null ? "" : String(detail.tenant.seat_limit));
      setTimezoneInput(detail.tenant.timezone ?? "UTC");
      setWeekStartInput(detail.tenant.week_start_day ?? "monday");
      setFiscalStartInput(detail.tenant.fiscal_year_start_month ?? 1);
      setAiHourlyLimitInput(
        detail.tenant.ai_calls_per_hour_limit == null ? "" : String(detail.tenant.ai_calls_per_hour_limit)
      );
      setAiMonthlyTokenInput(
        detail.tenant.ai_monthly_token_limit == null ? "" : String(detail.tenant.ai_monthly_token_limit)
      );
      setInitialAdmin(ia.tenant_admin);
      setLatestInvite(ia.latest_invite);
      setInitialAdminEmail(ia.tenant_admin?.email ?? "");
      await refreshConnectors();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  async function refreshConnectors() {
    if (!tenantId || !tenantHeaders) return;
    setConnectorsLoading(true);
    setConnectorsError(null);
    try {
      const data = await apiFetch<{ connectors: Connector[] }>("/api/connectors", {
        headers: tenantHeaders
      });
      setConnectors(data.connectors);
      setSelectedConnectorId((prev) =>
        prev && data.connectors.some((c) => c.id === prev) ? prev : data.connectors[0]?.id ?? null
      );
    } catch (e) {
      setConnectorsError(e instanceof Error ? e.message : String(e));
    } finally {
      setConnectorsLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  useEffect(() => {
    if (loading) return;
    if (window.location.hash !== "#connectors") return;
    const el = document.getElementById("connectors");
    if (el) el.scrollIntoView({ block: "start" });
  }, [loading]);

  async function save() {
    if (!tenantId) return;
    if (seatLimitParsed === "invalid") {
      setError("Seat limit must be empty (unlimited) or a non-negative integer.");
      return;
    }

    const tzTrim = timezoneInput.trim();
    if (!tzTrim) {
      setError("Time zone is required.");
      return;
    }

    let aiHourly: number | null;
    if (aiHourlyLimitInput.trim() === "") aiHourly = null;
    else {
      const n = Number(aiHourlyLimitInput);
      if (!Number.isInteger(n) || n < 1) {
        setError("AI calls/hour limit must be empty (unlimited) or a positive integer.");
        return;
      }
      aiHourly = n;
    }

    let aiMonthly: number | null;
    if (aiMonthlyTokenInput.trim() === "") aiMonthly = null;
    else {
      const n = Number(aiMonthlyTokenInput);
      if (!Number.isInteger(n) || n < 1) {
        setError("AI monthly token limit must be empty (unlimited) or a positive integer.");
        return;
      }
      aiMonthly = n;
    }

    setSaving(true);
    setError(null);
    try {
      const data = await apiFetch<{ tenant: Tenant; seats: SeatUsage }>(`/api/admin/tenants/${tenantId}`, {
        method: "PATCH",
        body: {
          name,
          auth_mode: authMode,
          billing_contact_name: billingName.trim() === "" ? null : billingName.trim(),
          billing_contact_email: billingEmail.trim() === "" ? null : billingEmail.trim(),
          seat_limit: seatLimitParsed,
          timezone: tzTrim,
          week_start_day: weekStartInput,
          fiscal_year_start_month: fiscalStartInput,
          ai_calls_per_hour_limit: aiHourly,
          ai_monthly_token_limit: aiMonthly
        }
      });
      setTenant(data.tenant);
      setSeats(data.seats);
      toast.push({ kind: "success", message: "Tenant updated." });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  // Break-glass contacts are tenant-managed; shown here read-only for operator visibility.

  const seatsLabel =
    seats == null
      ? "—"
      : tenant?.seat_limit == null
        ? `${seats.seat_used} used (unlimited)`
        : `${seats.seat_used} / ${tenant.seat_limit} used`;

  return (
    <div>
      <PageLayout
        title="Tenant details"
        actions={
          <div className="actions">
            <Link to="/admin/tenants" className="btn">
              ← Back
            </Link>
            <button
              onClick={() => void save()}
              disabled={
                loading ||
                saving ||
                !tenantId ||
                seatLimitParsed === "invalid"
              }
              className="btn btnPrimary"
            >
              Save changes
            </button>
          </div>
        }
      >
        {error && <ErrorState detail={error} />}
        {loading && <LoadingState />}

        {!loading && tenant && (
          <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
            <div className="card">
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Tenant info</div>
              <div style={{ display: "grid", gap: 8 }}>
                <label className="field">
                  <span className="label">Tenant ID</span>
                  <input className="input mono" value={tenant.id} readOnly />
                </label>

                <label className="field">
                  <span className="label">Name</span>
                  <input
                    className="input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </label>

                <label className="field">
                  <span className="label">Slug</span>
                  <input className="input mono" value={tenant.slug} readOnly />
                  <div className="help">The URL identifier is fixed once the tenant is created.</div>
                </label>

                <label className="field">
                  <span className="label">Auth mode</span>
                  <select
                    className="select"
                    value={authMode}
                    onChange={(e) => setAuthMode(e.target.value as any)}
                  >
                    <option value="atlas_local">atlas_local</option>
                    <option value="oidc">oidc</option>
                    <option value="saml">saml</option>
                  </select>
                </label>

                <div className="help">
                  Created: <span className="mono">{tenant.created_at}</span>
                </div>
              </div>
            </div>

            <div className="card">
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Seat pool</div>
              <div className="muted" style={{ marginBottom: 8 }}>
                Seats used counts tenant users in <code>active</code> or <code>invited</code> status.
              </div>
              <div style={{ display: "grid", gap: 8 }}>
                <div className="muted">
                  Current usage: <b>{seatsLabel}</b>
                </div>
                <label className="field">
                  <span className="label">Seat limit (empty = unlimited)</span>
                  <input
                    className="input"
                    value={seatLimitInput}
                    onChange={(e) => setSeatLimitInput(e.target.value)}
                    inputMode="numeric"
                    placeholder="e.g. 25"
                  />
                </label>
              </div>
            </div>

            <div className="card">
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Regional, calendar & AI limits</div>
              <div className="muted" style={{ marginBottom: 8, fontSize: 13 }}>
                US time zones only for the picker; existing non-US values are shown until changed. Reports count weeks, quarters and years from the
                calendar settings. Empty AI limit fields mean no cap.
              </div>
              <div style={{ display: "grid", gap: 8 }}>
                <label className="field">
                  <span className="label">Time zone</span>
                  <UsTimeZoneSelect value={timezoneInput} onChange={setTimezoneInput} />
                </label>
                <label className="field">
                  <span className="label">Week starts on</span>
                  <select className="select" value={weekStartInput} onChange={(e) => setWeekStartInput(e.target.value)}>
                    {WEEK_DAYS.map((d) => (
                      <option key={d} value={d}>
                        {d[0]!.toUpperCase() + d.slice(1)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span className="label">Fiscal year starts in</span>
                  <select className="select" value={fiscalStartInput} onChange={(e) => setFiscalStartInput(Number(e.target.value))}>
                    {MONTHS.map((m, i) => (
                      <option key={m} value={i + 1}>
                        {i === 0 ? "January (calendar year)" : m}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span className="label">AI successful calls per hour (empty = unlimited)</span>
                  <input
                    className="input"
                    value={aiHourlyLimitInput}
                    onChange={(e) => setAiHourlyLimitInput(e.target.value)}
                    inputMode="numeric"
                    placeholder="e.g. 120"
                  />
                </label>
                <label className="field">
                  <span className="label">AI tokens per calendar month (empty = unlimited)</span>
                  <input
                    className="input"
                    value={aiMonthlyTokenInput}
                    onChange={(e) => setAiMonthlyTokenInput(e.target.value)}
                    inputMode="numeric"
                    placeholder="e.g. 10000000"
                  />
                </label>
              </div>
            </div>

            <div className="card">
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Billing contact</div>
              <div style={{ display: "grid", gap: 8 }}>
                <label className="field">
                  <span className="label">Name</span>
                  <input
                    className="input"
                    value={billingName}
                    onChange={(e) => setBillingName(e.target.value)}
                  />
                </label>
                <label className="field">
                  <span className="label">Email</span>
                  <input
                    className="input"
                    value={billingEmail}
                    onChange={(e) => setBillingEmail(e.target.value)}
                    autoComplete="email"
                  />
                </label>
              </div>
            </div>

            <div className="card">
              <div className="toolbar" style={{ marginBottom: 10, flexWrap: "wrap" }}>
                <div style={{ fontWeight: 700 }}>AI usage</div>
                <div className="toolbarGroup">
                  <Link className="btn btnSm" to={`/admin/tenants/${tenant.id}/ai-usage`}>
                    Full page
                  </Link>
                </div>
              </div>
              <AiUsageDashboard
                embedded
                usagePath={`/api/admin/tenants/${encodeURIComponent(tenant.id)}/ai/usage`}
                logPath={`/api/admin/tenants/${encodeURIComponent(tenant.id)}/ai/usage/log`}
              />
            </div>

            <div className="card">
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Break-glass contacts</div>
              <div className="muted" style={{ marginBottom: 10 }}>
                Managed by the tenant (admin). Operators can view but not edit.
              </div>
              <BreakGlassContactsReadOnly contacts={contacts} />
            </div>

            <div id="connectors" className="card">
              <div className="toolbar" style={{ marginBottom: 10 }}>
                <div style={{ fontWeight: 700 }}>Connectors</div>
                <div className="toolbarGroup">
                  <button
                    className="btn btnPrimary"
                    type="button"
                    onClick={() => {
                      setConnectorModalOpen(true);
                      setConnectorName("");
                      setCreatedConnector(null);
                      setConnectorsError(null);
                    }}
                  >
                    + Create connector
                  </button>
                </div>
              </div>

              {connectorsError ? <ErrorState detail={connectorsError} /> : null}
              {connectorsLoading ? <LoadingState /> : null}

              {!connectorsLoading && (
                <div className="tableWrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>ID</th>
                        <th>Status</th>
                        <th>Kind</th>
                        <th>Dialect</th>
                        <th>Default</th>
                        <th>Last heartbeat</th>
                        <th>Last poll</th>
                        <th>Last execution</th>
                        <th>Version</th>
                        <th>Protocol</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {connectors.map((c) => (
                        <ConnectorRow
                          key={c.id}
                          connector={c}
                          tenantHeaders={tenantHeaders}
                          onUpdate={refreshConnectors}
                          onError={(msg) => setConnectorsError(msg)}
                          toast={toast}
                          onViewLogs={(id) => setSelectedConnectorId(id)}
                          onRestartNotice={(message) => setRestartNotice(message)}
                        />
                      ))}
                      {connectors.length === 0 && (
                        <tr>
                          <td colSpan={11} className="muted">
                            No connectors yet. Create one to connect this tenant's Plex system.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
              {restartNotice ? <div className="muted" style={{ marginTop: 10 }}>{restartNotice}</div> : null}
              {!connectorsLoading && (() => {
                const selected = connectors.find((c) => c.id === selectedConnectorId);
                if (!selected || !tenantHeaders || !selected.can_manage_actions) return null;
                return (
                  <div style={{ marginTop: 12 }}>
                    <ConnectorEventLog
                      connectorId={selected.id}
                      connectorName={selected.name}
                      connectorStatus={selected.status}
                      headers={tenantHeaders}
                    />
                  </div>
                );
              })()}
            </div>

            <div className="card">
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Tenant Administrator</div>

              <div style={{ display: "grid", gap: 10, marginBottom: 12 }}>
                <div className="row" style={{ alignItems: "center", justifyContent: "space-between" }}>
                  <div className="muted">
                    Status:{" "}
                    {initialAdmin ? <span className="pill">{initialAdmin.status}</span> : <span className="pill pillNeutral">not set</span>}
                  </div>
                  {latestInvite ? (
                    <div className="muted" style={{ fontSize: 12 }}>
                      Latest invite: <span className="mono">{latestInvite.created_at}</span>
                    </div>
                  ) : (
                    <div className="muted" style={{ fontSize: 12 }}>No invite sent yet.</div>
                  )}
                </div>

                <label className="field">
                  <span className="label">Admin email</span>
                  <input
                    className="input"
                    value={initialAdminEmail}
                    onChange={(e) => setInitialAdminEmail(e.target.value)}
                    autoComplete="email"
                    disabled={saving}
                  />
                </label>
              </div>

              <div className="actions">
                <button
                  className="btn"
                  type="button"
                  disabled={saving || !tenantId || !initialAdmin || initialAdmin.status !== "invited"}
                  onClick={async () => {
                    if (!tenantId) return;
                    setSaving(true);
                    setError(null);
                    try {
                      const res = await apiFetch<{ ok: true; invite_email_sent: boolean; invite: LatestInvite }>(
                        `/api/admin/tenants/${tenantId}/tenant-admin/resend-invite`,
                        { method: "POST" }
                      );
                      setLatestInvite(res.invite);
                      toast.push({
                        kind: res.invite_email_sent ? "success" : "error",
                        message: res.invite_email_sent ? "Invite sent." : "Invite could not be sent (mailer failure)."
                      });
                    } catch (e) {
                      setError(e instanceof Error ? e.message : String(e));
                    } finally {
                      setSaving(false);
                    }
                  }}
                >
                  Resend invite
                </button>
                <button
                  className="btn btnPrimary"
                  type="button"
                  disabled={saving || !tenantId || initialAdminEmail.trim() === "" || initialAdminEmail.trim().toLowerCase() === (initialAdmin?.email ?? "")}
                  onClick={async () => {
                    if (!tenantId) return;
                    setSaving(true);
                    setError(null);
                    try {
                      const res = await apiFetch<{ ok: true; invite_email_sent: boolean; invite: LatestInvite }>(
                        `/api/admin/tenants/${tenantId}/tenant-admin`,
                        { method: "PATCH", body: { email: initialAdminEmail.trim().toLowerCase() } }
                      );
                      setLatestInvite(res.invite);
                      toast.push({
                        kind: res.invite_email_sent ? "success" : "error",
                        message: res.invite_email_sent ? "Invite sent." : "Invite could not be sent (mailer failure)."
                      });
                      await refresh();
                    } catch (e) {
                      setError(e instanceof Error ? e.message : String(e));
                    } finally {
                      setSaving(false);
                    }
                  }}
                >
                  Save + send invite
                </button>
              </div>
            </div>

            <div className="card">
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Delete tenant</div>
              <div className="help" style={{ marginBottom: 12 }}>
                This permanently deletes the tenant and all associated data. To confirm, type the tenant name:{" "}
                <span className="mono">{tenant.name}</span>
              </div>

              <div style={{ display: "grid", gap: 10, marginBottom: 12 }}>
                <label className="field">
                  <span className="label">Type tenant name to confirm</span>
                  <input className="input" value={deleteName} onChange={(e) => setDeleteName(e.target.value)} />
                </label>
              </div>

              <div className="actions">
                <button
                  className="btn btnDanger"
                  type="button"
                  disabled={saving || deleteName.trim() !== tenant.name}
                  onClick={async () => {
                    if (!tenantId) return;
                    const ok = window.confirm(`Delete tenant "${tenant.name}"? This cannot be undone.`);
                    if (!ok) return;
                    setSaving(true);
                    setError(null);
                    try {
                      await apiFetch(`/api/admin/tenants/${tenantId}`, { method: "DELETE" });
                      toast.push({ kind: "success", message: "Tenant deleted." });
                      nav("/admin/tenants");
                    } catch (e) {
                      setError(e instanceof Error ? e.message : String(e));
                    } finally {
                      setSaving(false);
                    }
                  }}
                >
                  Delete tenant
                </button>
              </div>
            </div>
          </div>
        )}
      </PageLayout>

      {connectorModalOpen && (
        <div className="modalOverlay" role="presentation" onClick={() => setConnectorModalOpen(false)}>
          <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            {!createdConnector ? (
              <>
                <div style={{ fontWeight: 800, marginBottom: 10 }}>Create Connector</div>
                <div className="field" style={{ marginBottom: 12 }}>
                  <span className="label">Name</span>
                  <input
                    className="input"
                    value={connectorName}
                    onChange={(e) => setConnectorName(e.target.value)}
                    placeholder="Production Plex"
                    autoFocus
                  />
                </div>
                <div className="actions">
                  <button className="btn" onClick={() => setConnectorModalOpen(false)} disabled={creatingConnector}>
                    Cancel
                  </button>
                  <button
                    className="btn btnPrimary"
                    disabled={creatingConnector || connectorName.trim() === "" || !tenantHeaders}
                    onClick={async () => {
                      if (!tenantHeaders) return;
                      setCreatingConnector(true);
                      setConnectorsError(null);
                      try {
                        const data = await apiFetch<{ connector: CreatedConnector }>("/api/connectors", {
                          method: "POST",
                          headers: tenantHeaders,
                          body: { name: connectorName.trim() }
                        });
                        setCreatedConnector(data.connector);
                        await refreshConnectors();
                      } catch (e) {
                        setConnectorsError(e instanceof Error ? e.message : String(e));
                      } finally {
                        setCreatingConnector(false);
                      }
                    }}
                  >
                    Create
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontWeight: 800, marginBottom: 10 }}>Setup Instructions</div>
                <div className="muted" style={{ marginBottom: 10 }}>
                  Add these values to your connector .env file:
                </div>
                <pre className="card mono" style={{ margin: 0, whiteSpace: "pre-wrap" }}>
{`CONNECTOR_ID=${createdConnector.id}
TENANT_ID=${tenantId ?? ""}`}
                </pre>
                <div className="actions" style={{ marginTop: 12 }}>
                  <button
                    className="btn"
                    onClick={async () => {
                      try {
                        await copyToClipboard(
                          `CONNECTOR_ID=${createdConnector.id}\nTENANT_ID=${tenantId ?? ""}`
                        );
                        toast.push({ kind: "success", message: "Copied." });
                      } catch {
                        toast.push({ kind: "error", message: "Could not copy to clipboard." });
                      }
                    }}
                  >
                    Copy to clipboard
                  </button>
                  <button
                    className="btn btnPrimary"
                    onClick={() => {
                      setConnectorModalOpen(false);
                      setConnectorName("");
                      setCreatedConnector(null);
                    }}
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ConnectorRow(props: {
  connector: Connector;
  tenantHeaders: Record<string, string> | null;
  onUpdate: () => Promise<void>;
  onError: (msg: string) => void;
  toast: ReturnType<typeof useToast>;
  onViewLogs: (connectorId: string) => void;
  onRestartNotice: (message: string | null) => void;
}) {
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(props.connector.name);
  const [savingName, setSavingName] = useState(false);
  const [savingDialect, setSavingDialect] = useState(false);
  const [savingKind, setSavingKind] = useState(false);
  const [savingDefault, setSavingDefault] = useState(false);

  useEffect(() => {
    setNameValue(props.connector.name);
  }, [props.connector.name]);

  async function saveName() {
    if (!props.tenantHeaders || nameValue.trim() === props.connector.name || nameValue.trim() === "") return;
    setSavingName(true);
    try {
      await apiFetch<{ connector: Connector }>(`/api/connectors/${props.connector.id}`, {
        method: "PATCH",
        headers: props.tenantHeaders,
        body: { name: nameValue.trim() }
      });
      props.toast.push({ kind: "success", message: "Connector name updated." });
      setEditingName(false);
      await props.onUpdate();
    } catch (e) {
      props.onError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingName(false);
    }
  }

  async function saveKind(value: ConnectorKind) {
    if (!props.tenantHeaders) return;
    if (props.connector.kind === value) return;
    setSavingKind(true);
    try {
      await apiFetch<{ connector: Connector }>(`/api/connectors/${props.connector.id}`, {
        method: "PATCH",
        headers: props.tenantHeaders,
        body: { kind: value }
      });
      props.toast.push({ kind: "success", message: "Connector kind updated." });
      await props.onUpdate();
    } catch (e) {
      props.onError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingKind(false);
    }
  }

  async function saveDialect(value: ConnectorDialect | null) {
    if (!props.tenantHeaders) return;
    if ((props.connector.dialect ?? null) === value) return;
    setSavingDialect(true);
    try {
      await apiFetch<{ connector: Connector }>(`/api/connectors/${props.connector.id}`, {
        method: "PATCH",
        headers: props.tenantHeaders,
        body: { dialect: value }
      });
      props.toast.push({ kind: "success", message: "Dialect updated." });
      await props.onUpdate();
    } catch (e) {
      props.onError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingDialect(false);
    }
  }

  async function makeDefault() {
    if (!props.tenantHeaders || props.connector.is_default) return;
    setSavingDefault(true);
    try {
      await apiFetch<{ connector: Connector }>(`/api/connectors/${props.connector.id}`, {
        method: "PATCH",
        headers: props.tenantHeaders,
        body: { is_default: true }
      });
      props.toast.push({ kind: "success", message: `'${props.connector.name}' is now the default connector.` });
      await props.onUpdate();
    } catch (e) {
      props.onError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingDefault(false);
    }
  }

  async function copyUuid() {
    try {
      await copyToClipboard(props.connector.id);
      props.toast.push({ kind: "success", message: "Connector ID copied." });
    } catch {
      props.toast.push({ kind: "error", message: "Could not copy to clipboard." });
    }
  }

  return (
    <tr>
      <td>
        {editingName ? (
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input
              className="input"
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void saveName();
                if (e.key === "Escape") {
                  setNameValue(props.connector.name);
                  setEditingName(false);
                }
              }}
              autoFocus
              disabled={savingName}
              style={{ width: 200 }}
            />
            <button
              className="btn btnSm"
              onClick={() => void saveName()}
              disabled={savingName || nameValue.trim() === "" || nameValue.trim() === props.connector.name}
            >
              Save
            </button>
            <button
              className="btn btnSm"
              onClick={() => {
                setNameValue(props.connector.name);
                setEditingName(false);
              }}
              disabled={savingName}
            >
              Cancel
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <span>{props.connector.name}</span>
            <button
              className="btn btnSm"
              onClick={() => setEditingName(true)}
              style={{ padding: "4px 8px", fontSize: 11 }}
            >
              Edit
            </button>
          </div>
        )}
      </td>
      <td>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span className="mono" title={props.connector.id}>
            {connectorIdShort(props.connector.id)}
          </span>
          <button
            className="btn btnSm"
            onClick={() => void copyUuid()}
            style={{ padding: "4px 8px", fontSize: 11 }}
            title="Copy full UUID"
          >
            Copy
          </button>
        </div>
      </td>
      <td>
        <span className={STATUS_PILL_CLASSES[props.connector.status]}>
          {STATUS_LABELS[props.connector.status]}
        </span>
      </td>
      <td>
        {/*
          ADR-0161 — Kind decides which platform knowledge pack serves to this
          tenant's query sessions. No "— none —" option: kind is total on the
          row, unlike dialect. Choosing a kind whose dialect implication applies
          lets the SERVER fill the dialect (§5.1.3) — the refreshed row shows it,
          and the rule is deliberately not duplicated client-side.
        */}
        <select
          className="select"
          value={props.connector.kind}
          disabled={savingKind || !props.tenantHeaders}
          onChange={(e) => void saveKind(e.target.value as ConnectorKind)}
          aria-label={`Kind for ${props.connector.name}`}
        >
          {CONNECTOR_KIND_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </td>
      <td>
        <select
          className="select"
          value={props.connector.dialect ?? ""}
          disabled={savingDialect || !props.tenantHeaders}
          onChange={(e) => {
            const v = e.target.value === "" ? null : (e.target.value as ConnectorDialect);
            void saveDialect(v);
          }}
          aria-label={`Dialect for ${props.connector.name}`}
        >
          <option value="">— none —</option>
          {CONNECTOR_DIALECT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </td>
      <td>
        {props.connector.is_default ? (
          <span className="pill pillOk">Default</span>
        ) : (
          <button
            className="btn btnSm"
            type="button"
            disabled={savingDefault || !props.tenantHeaders}
            onClick={() => void makeDefault()}
          >
            Make default
          </button>
        )}
      </td>
      <td className="muted">{formatTimestampOr(props.connector.last_heartbeat_at, "Never")}</td>
      <td className="muted">{formatTimestampOr(props.connector.last_poll_at, "Never")}</td>
      <td className="muted">{formatTimestampOr(props.connector.last_execution_completed_at ?? props.connector.last_execution_assigned_at, "Never")}</td>
      <td className="muted mono">{props.connector.version}</td>
      <td className="muted mono">{props.connector.protocol}</td>
      <td>
        <div className="actions">
          {props.connector.can_manage_actions ? (
            <>
              <button className="btn btnSm" type="button" onClick={() => props.onViewLogs(props.connector.id)}>
                Logs
              </button>
              <button
                className="btn btnSm"
                type="button"
                onClick={async () => {
                  if (!props.tenantHeaders) return;
                  const ok = window.confirm(`Restart '${props.connector.name}'? This will briefly interrupt executions.`);
                  if (!ok) return;
                  try {
                    const data = await apiFetch<{ command: { status: string } }>(`/api/connectors/${props.connector.id}/restart`, {
                      method: "POST",
                      headers: props.tenantHeaders
                    });
                    props.toast.push({ kind: "success", message: "Restart requested." });
                    props.onRestartNotice(
                      data.command.status !== "acked" ? "Restart requested (waiting for connector)." : null
                    );
                    await props.onUpdate();
                  } catch (e) {
                    props.onError(e instanceof Error ? e.message : String(e));
                  }
                }}
              >
                Restart
              </button>
            </>
          ) : null}
          <button
            className="btn"
            type="button"
            onClick={async () => {
              if (!props.tenantHeaders) return;
              const ok = window.confirm(`Delete connector '${props.connector.name}' (${connectorIdShort(props.connector.id)})? This cannot be undone.`);
              if (!ok) return;
              try {
                await apiFetch<{ ok: true }>(`/api/connectors/${props.connector.id}`, {
                  method: "DELETE",
                  headers: props.tenantHeaders
                });
                props.toast.push({ kind: "success", message: "Connector deleted." });
                await props.onUpdate();
              } catch (e) {
                props.onError(e instanceof Error ? e.message : String(e));
              }
            }}
          >
            Delete
          </button>
        </div>
      </td>
    </tr>
  );
}

function BreakGlassContactsReadOnly(props: { contacts: BreakGlassContact[] }) {
  return (
    <div>
      <div style={{ display: "grid", gap: 8 }}>
        {props.contacts.map((c) => (
          <div
            key={c.id}
            style={{ border: "1px solid var(--border)", borderRadius: 10, padding: 10, background: "var(--panel)" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div style={{ fontWeight: 600 }}>{c.email}</div>
              <div className="muted">{c.verified_at ? "verified" : "unverified"}</div>
            </div>
          </div>
        ))}
        {props.contacts.length === 0 ? <div className="muted">No contacts yet.</div> : null}
      </div>
    </div>
  );
}

